<template>
  <FormCustomFields
    v-model:field-values="localFieldValues"
    :context-message="contextMessage"
    :loading="loading"
    :fields="fields"
    :errors="errors"
    :force-enable-inputs="props.forceEnableInputs"
  />
</template>

<script setup lang="ts">
import { watch, computed } from 'vue'
import { CustomFieldTypeValueInput, TypeSupportingCustomFieldValues } from '@/gql/graphql'
import { FieldableFormCustomFieldFragment } from '@/components/Fieldable/graphql/fragments'
import useFieldableCustomFields from '@/components/Fieldable/composables/useFieldableCustomFields'
import { useFieldableFormStore } from '@/components/Fieldable/store/useFieldableFormStore'
import { FragmentType, useFragment } from '@/gql'
import FormCustomFields from '@/components/Fieldable/FormCustomFields.vue'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'

const emits = defineEmits(['update:field-values'])

interface Props {
  accountId: string
  fieldableType: TypeSupportingCustomFieldValues
  fieldValues: CustomFieldTypeValueInput[]
  fieldableId?: string
  /*
   * Controls whether errors should be displayed.
   * This gives control to the parent component.
   */
  shouldDisplayErrors?: boolean
  contextMessage?: string
  forceEnableInputs?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  fieldableId: undefined,
  shouldDisplayErrors: false,
  forceEnableInputs: false,
})

const { fields, loading } = useFieldableCustomFields(
  props.accountId,
  [props.accountId],
  [props.fieldableType],
  PER_PAGE_MAXIMUM,
  true,
)

const fieldableFormStore = useFieldableFormStore()

const localFieldValues = computed<CustomFieldTypeValueInput[]>({
  get: () => props.fieldValues,
  set: (newFieldValues) => {
    emits('update:field-values', newFieldValues)
  },
})

const updateFieldsOnStore = (
  newFieldsForStore: FragmentType<typeof FieldableFormCustomFieldFragment>[],
) => {
  fieldableFormStore.updateFields(
    newFieldsForStore.map((field) => useFragment(FieldableFormCustomFieldFragment, field)),
  )
}

watch(fields, (newFields) => {
  updateFieldsOnStore(newFields)
})

watch(
  () => props.fieldValues,
  (newFieldValues) => {
    fieldableFormStore.updateFieldValues(newFieldValues)
  },
  { immediate: true },
)

const errors = computed(() => (props.shouldDisplayErrors ? fieldableFormStore.errors : {}))
</script>
